<template>
  <transition name="save-name-dialog">
    <div
      v-if="visible"
      class="save-name-dialog"
      role="dialog"
      aria-modal="true"
      :aria-label="stage === 'question' ? '问卷' : (stage === 'hint' ? '保存提示' : '保存沙盘')"
      @keydown.esc.prevent.stop="onCancel"
    >
      <button
        type="button"
        class="save-name-dialog__scrim"
        aria-label="关闭"
        @click="onCancel"
      />
      <div class="save-name-dialog__card">
        <!-- 阶段 1：输入名称 -->
        <div v-if="stage === 'name'" class="save-name-dialog__view">
          <div class="save-name-dialog__hero" aria-hidden="true">
            <div class="save-name-dialog__hero-icon">
              <svg viewBox="0 0 64 64" width="44" height="44">
                <defs>
                  <linearGradient id="saveNameFlower" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stop-color="#ff8fb1"/>
                    <stop offset="100%" stop-color="#ff5c8a"/>
                  </linearGradient>
                </defs>
                <circle cx="32" cy="32" r="10" fill="url(#saveNameFlower)"/>
                <circle cx="32" cy="14" r="8" fill="url(#saveNameFlower)" opacity="0.85"/>
                <circle cx="32" cy="50" r="8" fill="url(#saveNameFlower)" opacity="0.85"/>
                <circle cx="14" cy="32" r="8" fill="url(#saveNameFlower)" opacity="0.85"/>
                <circle cx="50" cy="32" r="8" fill="url(#saveNameFlower)" opacity="0.85"/>
                <circle cx="32" cy="32" r="5" fill="#fff7d6"/>
              </svg>
            </div>
            <h2 class="save-name-dialog__title">保存沙盘</h2>
            <div class="save-name-dialog__subtitle">为本次作品命名，留下专属回忆</div>
          </div>

          <p class="save-name-dialog__desc">请输入本次作品的名称，保存后将自动返回主页。</p>

          <label class="save-name-dialog__field" for="save-name-input">
            <span class="save-name-dialog__label">作品名称</span>
            <input
              id="save-name-input"
              ref="nameInput"
              v-model.trim="localName"
              type="text"
              class="save-name-dialog__input"
              placeholder="请输入存档名称"
              maxlength="40"
              :disabled="loading"
              @keydown.enter.prevent.stop="onGoNext"
            />
          </label>

          <div class="save-name-dialog__quota" aria-live="polite">
            <span class="save-name-dialog__quota-label">剩余可用次数</span>
            <span
              class="save-name-dialog__quota-value"
              :class="{ 'is-empty': remaining <= 0 }"
            >{{ remaining }}/{{ totalQuota }}</span>
          </div>

          <div class="save-name-dialog__actions">
            <button
              type="button"
              class="save-name-dialog__btn save-name-dialog__btn--ghost"
              :disabled="loading"
              @click="onCancel"
            >
              取消
            </button>
            <button
              type="button"
              class="save-name-dialog__btn save-name-dialog__btn--primary"
              :disabled="loading || !localName || remaining <= 0"
              @click="onConfirm"
            >
              {{ loading ? loadingText : '确定' }}
            </button>
          </div>
        </div>

        <!-- 问卷阶段 -->
        <div v-else-if="stage === 'question'" class="save-name-dialog__view">
          <div class="save-name-dialog__quest-header" aria-hidden="true">
            <div class="save-name-dialog__quest-icon">
              <svg viewBox="0 0 64 64" width="40" height="40" fill="none">
                <circle cx="32" cy="32" r="28" fill="#e8f5e9"/>
                <circle cx="32" cy="28" r="10" fill="#66bb6a"/>
                <path d="M32 44 L28 40 L30 40 L30 36 L34 36 L34 40 L36 40 Z" fill="#66bb6a"/>
              </svg>
            </div>
          </div>

          <!-- 进度条 -->
          <div class="save-name-dialog__progress" aria-label="答题进度">
            <div
              class="save-name-dialog__progress-bar"
              :style="{ width: progressPercent + '%' }"
            />
          </div>
          <div class="save-name-dialog__progress-label">第 {{ currentQuestionIndex + 1 }} / {{ questionCount }} 题</div>

          <!-- 题干 -->
          <p class="save-name-dialog__quest-text">{{ currentQuestion.text }}</p>

          <!-- 选项列表（radio 单选） -->
          <div class="save-name-dialog__options" role="radiogroup" :aria-label="currentQuestion.text">
            <label
              v-for="(opt, idx) in currentQuestion.options"
              :key="idx"
              class="save-name-dialog__option"
              :class="{ 'is-selected': answers[currentQuestion.id] === idx }"
            >
              <input
                type="radio"
                class="save-name-dialog__option-radio"
                :name="'quest-' + currentQuestion.id"
                :value="idx"
                :checked="answers[currentQuestion.id] === idx"
                @change="selectOption(idx)"
              />
              <span class="save-name-dialog__option-check" aria-hidden="true">
                <span v-if="answers[currentQuestion.id] === idx" class="save-name-dialog__option-dot"/>
              </span>
              <span class="save-name-dialog__option-text">{{ opt }}</span>
            </label>
          </div>

          <!-- 底部按钮 -->
          <div class="save-name-dialog__actions">
            <button
              type="button"
              class="save-name-dialog__btn save-name-dialog__btn--ghost"
              :disabled="loading || currentQuestionIndex === 0"
              @click="goBackQuestion"
            >
              上一题
            </button>
            <button
              type="button"
              class="save-name-dialog__btn save-name-dialog__btn--primary"
              :disabled="loading || answers[currentQuestion.id] == null"
              @click="isLastQuestion ? onFinish() : goNextQuestion()"
            >
              {{ isLastQuestion ? '完成' : '下一题' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'SaveNameDialog',
  props: {
    visible: { type: Boolean, default: false },
    initialName: { type: String, default: '' },
    remaining: { type: Number, default: 20 },
    totalQuota: { type: Number, default: 20 },
    loading: { type: Boolean, default: false },
    loadingText: { type: String, default: '保存中…' },
    /** 用户年龄，用于判断是否显示问卷 */
    age: { type: Number, default: 0 },
    /**
     * 问卷题目列表（18岁以下用户使用）。
     * 形如 [{ id: string, text: string, options: string[] }]
     */
    questions: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      localName: this.initialName,
      stage: 'name', // 'name' | 'question'
      answers: {}, // { [questionId]: selectedIndex (0-3) }
      currentQuestionIndex: 0
    }
  },
  computed: {
    questionCount () {
      return this.questions.length
    },
    currentQuestion () {
      return this.questions[this.currentQuestionIndex] || { id: '', text: '', options: [] }
    },
    isLastQuestion () {
      return this.currentQuestionIndex >= this.questionCount - 1
    },
    progressPercent () {
      if (this.questionCount === 0) return 0
      return Math.round(((this.currentQuestionIndex) / this.questionCount) * 100)
    }
  },
  watch: {
    visible (open) {
      if (open) {
        this.localName = this.initialName || ''
        this.stage = 'name'
        this.answers = {}
        this.currentQuestionIndex = 0
        this.$nextTick(() => {
          const el = this.$refs.nameInput
          if (el && typeof el.focus === 'function') {
            el.focus()
            if (el.select && this.localName) el.select()
          }
          document.addEventListener('keydown', this.onKeydown)
        })
      } else {
        document.removeEventListener('keydown', this.onKeydown)
      }
    }
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    onKeydown (e) {
      if (e.key === 'Escape' && this.visible && !this.loading) this.onCancel()
    },
    onCancel () {
      if (this.loading) return
      if (this.stage === 'question') {
        this.currentQuestionIndex = Math.max(0, this.currentQuestionIndex - 1)
        if (this.currentQuestionIndex === 0) this.stage = 'hint'
        return
      }
      this.$emit('cancel')
      this.$emit('update:visible', false)
    },
    selectOption (idx) {
      this.answers = { ...this.answers, [this.currentQuestion.id]: idx }
    },
    goNextQuestion () {
      if (this.isLastQuestion) return
      this.currentQuestionIndex++
    },
    goBackQuestion () {
      if (this.currentQuestionIndex === 0) {
        this.stage = 'name'
        return
      }
      this.currentQuestionIndex--
    },
    onFinish () {
      if (this.loading) return
      if (!this.localName) return
      this.$emit('confirm', this.localName, this.answers)
      this.$emit('update:visible', false)
    },
    onConfirm () {
      if (this.loading) return
      if (!this.localName) return
      // 有问卷题目时进入问卷阶段
      if (this.questions.length > 0) {
        this.stage = 'question'
        this.currentQuestionIndex = 0
        this.answers = {}
      } else {
        // 无问卷时直接保存
        this.$emit('confirm', this.localName, {})
        this.$emit('update:visible', false)
      }
    }
  }
}
</script>

<style scoped>
.save-name-dialog {
  position: fixed;
  inset: 0;
  z-index: 1310;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.save-name-dialog__scrim {
  position: absolute;
  inset: 0;
  border: none;
  background: rgba(8, 24, 48, 0.55);
  backdrop-filter: blur(2px);
  cursor: pointer;
}
.save-name-dialog__card {
  position: relative;
  z-index: 1;
  width: min(420px, 100%);
  padding: 0 24px 22px;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 20px 56px rgba(13, 71, 161, 0.28);
  text-align: left;
  overflow: hidden;
}

.save-name-dialog__view {
  animation: save-name-view-in 0.22s ease both;
}
@keyframes save-name-view-in {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

.save-name-dialog__hero {
  position: relative;
  padding: 26px 0 18px;
  margin: 0 -24px 18px;
  background:
    radial-gradient(circle at 20% 0%, rgba(255, 175, 199, 0.28) 0%, rgba(255, 175, 199, 0) 55%),
    radial-gradient(circle at 85% 90%, rgba(187, 222, 251, 0.32) 0%, rgba(187, 222, 251, 0) 55%),
    linear-gradient(180deg, #f4f8ff 0%, #ffffff 100%);
  text-align: center;
}

.save-name-dialog__hero::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  width: 36px;
  height: 3px;
  border-radius: 999px;
  background: linear-gradient(90deg, #42a5f5, #1565c0);
}

.save-name-dialog__hero-icon {
  width: 72px;
  height: 72px;
  margin: 0 auto 10px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow:
    0 8px 18px rgba(255, 92, 138, 0.18),
    inset 0 -2px 6px rgba(255, 175, 199, 0.18);
  animation: save-name-hero-bob 3.4s ease-in-out infinite;
  /* 装饰动画元素不参与 hit-testing，避免 iOS 上动画抢命中 */
  pointer-events: none;
}
.save-name-dialog__hero--cover {
  background:
    radial-gradient(circle at 80% 0%, rgba(187, 222, 251, 0.32) 0%, rgba(187, 222, 251, 0) 55%),
    radial-gradient(circle at 15% 90%, rgba(255, 236, 179, 0.32) 0%, rgba(255, 236, 179, 0) 55%),
    linear-gradient(180deg, #f4f8ff 0%, #ffffff 100%);
}
.save-name-dialog__hero-icon--cover {
  box-shadow:
    0 8px 18px rgba(66, 165, 245, 0.22),
    inset 0 -2px 6px rgba(187, 222, 251, 0.22);
}

.save-name-dialog__title {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 700;
  color: #0d47a1;
  letter-spacing: 0.5px;
}

.save-name-dialog__subtitle {
  font-size: 12px;
  color: #607d8b;
  letter-spacing: 0.4px;
}

@keyframes save-name-hero-bob {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}

.save-name-dialog__desc {
  margin: 0 0 18px;
  font-size: 13px;
  line-height: 1.7;
  color: #607d8b;
  text-align: center;
}
.save-name-dialog__field {
  display: block;
  margin: 0 0 12px;
}
.save-name-dialog__label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
}
.save-name-dialog__input {
  width: 100%;
  height: 42px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1.5px solid #cfd8dc;
  background: #f8fafc;
  font-size: 15px;
  color: #263238;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  box-sizing: border-box;
}
.save-name-dialog__input:focus {
  border-color: #1976d2;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.18);
}
.save-name-dialog__input:disabled {
  opacity: 0.6;
}
.save-name-dialog__quota {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 14px;
  margin-bottom: 20px;
  border-radius: 10px;
  background: #e3f2fd;
  font-size: 14px;
}
.save-name-dialog__quota-label { color: #1565c0; font-weight: 600; }
.save-name-dialog__quota-value {
  color: #0d47a1;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.save-name-dialog__quota-value.is-empty { color: #c62828; }

/* 阶段 2：纯文字提示视图 */
.save-name-dialog__hint {
  margin: 0 0 24px;
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.75;
  color: #455a64;
  text-align: center;
  background: #f4f8ff;
  border-radius: 10px;
  border: 1px dashed #bbdefb;
}

/* 横屏移动端适配 */
@media (max-height: 500px) and (orientation: landscape) {
  .save-name-dialog__card {
    padding-bottom: 14px;
  }
  .save-name-dialog__hero {
    padding: 12px 0 10px;
    margin-bottom: 10px;
  }
  .save-name-dialog__hero-icon {
    width: 48px;
    height: 48px;
    margin-bottom: 6px;
  }
  .save-name-dialog__hero-icon svg {
    width: 28px !important;
    height: 28px !important;
  }
  .save-name-dialog__hint {
    margin-bottom: 12px;
    padding: 10px 12px;
    font-size: 13px;
  }
  .save-name-dialog__actions {
    gap: 10px;
  }
}

/* 问卷阶段 */
.save-name-dialog__quest-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 -24px;
  padding: 18px 24px 10px;
  background: linear-gradient(180deg, #f1f8e9 0%, #ffffff 100%);
  border-radius: 18px 18px 0 0;
}
.save-name-dialog__quest-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  animation: save-name-hero-bob 3.4s ease-in-out infinite;
}

.save-name-dialog__progress {
  height: 4px;
  background: #e8f5e9;
  border-radius: 2px;
  margin: 12px 0 4px;
  overflow: hidden;
}
.save-name-dialog__progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #66bb6a 0%, #43a047 100%);
  border-radius: 2px;
  transition: width 0.3s ease;
}
.save-name-dialog__progress-label {
  font-size: 11px;
  color: #9e9e9e;
  text-align: center;
  margin-bottom: 14px;
}

.save-name-dialog__quest-text {
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  line-height: 1.6;
  text-align: center;
}

.save-name-dialog__options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
}

.save-name-dialog__option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  border-radius: 12px;
  border: 1.5px solid #e0e0e0;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.14s ease, background 0.14s ease, transform 0.12s ease;
  user-select: none;
}
.save-name-dialog__option:hover:not(.is-selected) {
  border-color: #a5d6a7;
  background: #f1f8e9;
}
.save-name-dialog__option.is-selected {
  border-color: #43a047;
  background: #e8f5e9;
}
.save-name-dialog__option:active {
  transform: scale(0.98);
}
.save-name-dialog__option-radio {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.save-name-dialog__option-check {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #bdbdbd;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.14s ease, background 0.14s ease;
}
.save-name-dialog__option.is-selected .save-name-dialog__option-check {
  border-color: #43a047;
  background: #43a047;
}
.save-name-dialog__option-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #fff;
}
.save-name-dialog__actions {
  display: flex;
  justify-content: center;
  gap: 12px;
}
.save-name-dialog__btn {
  min-width: 108px;
  height: 42px;
  padding: 0 20px;
  border-radius: 10px;
  border: 2px solid transparent;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.12s ease, transform 0.12s ease;
}
.save-name-dialog__btn:active:not(:disabled) { transform: scale(0.98); }
.save-name-dialog__btn:disabled { opacity: 0.55; cursor: not-allowed; }
.save-name-dialog__btn--ghost {
  background: #f5f7fa;
  color: #546e7a;
  border-color: #cfd8dc;
}
.save-name-dialog__btn--ghost:hover:not(:disabled) { background: #eceff1; }
.save-name-dialog__btn--primary {
  background: linear-gradient(180deg, #42a5f5 0%, #1565c0 100%);
  color: #fff;
  border-color: #1565c0;
}

.save-name-dialog-enter-active,
.save-name-dialog-leave-active {
  transition: opacity 0.2s ease;
}
.save-name-dialog-enter-active .save-name-dialog__card,
.save-name-dialog-leave-active .save-name-dialog__card {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.save-name-dialog-enter,
.save-name-dialog-leave-to { opacity: 0; }
.save-name-dialog-enter .save-name-dialog__card,
.save-name-dialog-leave-to .save-name-dialog__card {
  opacity: 0;
  transform: scale(0.94) translateY(8px);
}
</style>



// WEBPACK FOOTER //
// src/components/SaveNameDialog.vue